// External
import { CookieService } from "ngx-cookie-service";
import { Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { tv4 } from "tv4";

// Internal
import { POST, RequestsService } from "../../global/request-service/requests.service";
import { OperatingSystems, ValuesService } from "../../../values/values.service";
import { schemas } from "../../../../common/models/schemas";
import { ConfigService } from "../../../../common/config/config.service";
import { UtilsCommonService } from "../../../../common/utils/utils-common.service";
import { catchError, map } from "rxjs/operators";
import { SubscriptionsV3Prefix } from "../../../../common/values/subscriptions.values.service";
import { DownloadLinkInfo, SharedLinkInfo } from "../../../../common/modal-dialog/devices/install-modal/install-modal.model";

enum LinkType {
    PRODUCTION = 'production',
    INSIDER = 'insider'
}
@Injectable({
    providedIn: "root"
})
export class DownloadAgentService {
    constructor(
        private configService: ConfigService,
        private cookieService: CookieService,
        private requestsService: RequestsService,
        private utilsCommon: UtilsCommonService,
        private valuesService: ValuesService
    ) { }

    isV3Service(serviceId) {
        return serviceId?.includes(SubscriptionsV3Prefix);
    }

    /**
     * This method is used to return the download link with included bootstrap token to the requesting client
     * @param {object} Contains mandatory `platform` and `appId`
     * @returns Response from server
     */
    getLink(data): Observable<any> {
        if (
            (data.platform !== "windows" &&
            data.platform !== "osx" &&
            data.platform !== "android" &&
            data.platform !== "ios") ||
            !data.appId ||
            !this.utilsCommon.checkString(data.appId)
        ) {
            return throwError(() => new Error('Invalid params'));
        }
        const lang = this.cookieService
            .get(this.valuesService.cookieLang)
            .toLowerCase();
        let json = {
            id: 1,
            jsonrpc: "2.0",
            method: "get_link",
            params: {
                connect_source: {
                    user_token: this.cookieService.get(this.valuesService.cookieToken),
                    app_id: this.valuesService.connectAppId,
                    device_id: "web"
                },
                platform: data.platform,
                lang: lang,
                app_id: data.appId,
                type: this.configService.deployTypeIsProd() ? "production" : "insider"
            }
        };

        if (data.serviceId && !this.isV3Service(data?.serviceId)) {
            json.params["service_id"] = data.serviceId;
        }

        if (data.profile_id) {
            json.params["profile_id"]  = data.profile_id;
        }

        if (data.activityId) {
            json.params["activity_id"] = data.activityId;
        }

        return this.requestsService.make(
            this.configService.config.nimbusServer,
            this.valuesService.downloadAgentService,
            json,
            "POST"
        ).pipe(
            map(
                (res: any) => {
                    return res.result;
                }
            ),
            catchError((error) => {
                throw error;
            }),
        );
    }

    /**
     * This method is used to generate a link valid for validity seconds
     * @returns Response from server
     */
    getShareLink(data): Observable<any> {
        let json = {
            id: 1,
            jsonrpc: "2.0",
            method: "get_share_link",
            params: {
                connect_source: {
                    user_token: this.cookieService.get(this.valuesService.cookieToken),
                    app_id: this.valuesService.connectAppId,
                    device_id: "web"
                },
                profile_id: '',
                service_id: '',
                "product": data && data.product ? data.product : "protection",
                type: this.configService.deployTypeIsProd() ? "production" : "insider"
            }
        };

        if(data && data.profile_id){
            json.params.profile_id = data.profile_id;
        } else {
            delete json.params.profile_id;
        }

        if(data && data.service_id && !this.isV3Service(data?.service_id)){
            json.params.service_id = data.service_id;
        } else {
            delete json.params.service_id;
        }

        if (data && data.activityId) {
            json.params["activity_id"] = data.activityId;
        } else {
            delete json.params["activity_id"];
        }

        if (data && data.qr_code) {
            json.params["qr_code"] = data.qr_code;
        } else {
            delete json.params["qr_code"];
        }

        if (data && data.qr_code_type) {
            json.params["qr_code_type"] = data.qr_code_type;
        } else {
            delete json.params["qr_code_type"];
        }

        return this.requestsService.make(
            this.configService.config.nimbusServer,
            this.valuesService.downloadAgentService,
            json,
            "POST"
        ).pipe(
            map(
                (res: any) => {
                    res.result.link = this._replaceHostname(res.result.link);
                    return res.result;
                }
            ),
            catchError((error) => {
                throw error;
            }),
        )
    }

    /**
     * This method is used to send a share link via email
     * @param {object} Contains mandatory `mailSendLink`
     * @returns Response from server
     */
    sendShareLink(mailSendLink, info): Observable<any> {
        if (!tv4.validate(mailSendLink, schemas.schemaEMAIL)) {
            console.error("Invalid params", tv4.error);
            return throwError(() => new Error('test'));
        }
        const lang = this.cookieService
            .get(this.valuesService.cookieLang)
            .toLowerCase();
        let json = {
            id: 1,
            jsonrpc: "2.0",
            method: "send_share_link",
            params: {
                connect_source: {
                    user_token: this.cookieService.get(this.valuesService.cookieToken),
                    app_id: this.valuesService.connectAppId,
                    device_id: "web"
                },
                user_email: mailSendLink,
                lang: lang,
                type: this.configService.deployTypeIsProd() ? "production" : "insider",
                product: info && info.product ? info.product : "protection",
                profile_id: '',
                service_id: ''
            }
        };

        if(info.profile_id){
            json.params.profile_id = info.profile_id;
        } else {
            delete json.params.profile_id;
        }

        if(info && info.service_id && !this.isV3Service(info?.service_id)){
            json.params.service_id = info.service_id;
        } else {
            delete json.params.service_id;
        }

        return this.requestsService.make(
            this.configService.config.nimbusServer,
            this.valuesService.downloadAgentService,
            json,
            "POST"
        ).pipe(
            map(
                (res: any) => {
                    return res.result;
                }
            ),
            catchError((error) => {
                throw error;
            }),
        )
    }

    /**
     * This method is used to send a share link via email
     * @param {object} Contains mandatory `mailSendLink`
     * @returns Response from server
     */
    public sendShareLinkV2(mailSendLink: string, info: SharedLinkInfo): Observable<any> {
        if (!tv4.validate(mailSendLink, schemas.schemaEMAIL)) {
            console.error('Invalid params', tv4.error);
            return throwError(() => new Error('error'));
        }

        const lang = this.cookieService.get(this.valuesService.cookieLang).toLowerCase();

        const json: any = {
            id: 1,
            jsonrpc: this.valuesService.jsonrpc,
            method: 'send_share_link',
            params: {
                connect_source: {
                    user_token: this.cookieService.get(this.valuesService.cookieToken),
                    app_id: this.valuesService.connectAppId,
                    device_id: this.valuesService.connectDeviceId,
                },
                user_email: mailSendLink,
                lang: lang,
                type: this.configService.deployTypeIsProd() ? LinkType.PRODUCTION : LinkType.INSIDER,
            }
        };

        if (info?.profile_id){
            json.params.profile_id = info.profile_id;
        }

        if (info?.product){
            json.params.product = info.product;
        }

        if (info?.service_id && !this.isV3Service(info?.service_id)){
            json.params.service_id = info.service_id;
        }

        return this.requestsService.make(
            this.configService.config.nimbusServer,
            this.valuesService.downloadAgentService,
            json,
            POST
        ).pipe(
            map(
                (res: any) => {
                    return res.result;
                }
            ),
            catchError((error) => {
                throw error;
            }),
        );
    }

    /**
     * This method is used to return the download link with included bootstrap token to the requesting client
     * @param {DownloadLinkInfo} data Contains data for the request
     * @returns Response from server
     */
    public getLinkV2(data: DownloadLinkInfo): Observable<any> {
        if ((data.platform !== OperatingSystems.WINDOWS
                && data.platform !== OperatingSystems.OSX
                && data.platform !== OperatingSystems.ANDROID
                && data.platform !== OperatingSystems.IOS)
            || !data.app_id
            || !this.utilsCommon.checkString(data.app_id)) {
            return throwError(() => new Error('Invalid params'));
        }

        const lang = this.cookieService.get(this.valuesService.cookieLang).toLowerCase();
        const json: any = {
            id: 1,
            jsonrpc: this.valuesService.jsonrpc,
            method: 'get_link',
            params: {
                connect_source: {
                    user_token: this.cookieService.get(this.valuesService.cookieToken),
                    app_id: this.valuesService.connectAppId,
                    device_id: this.valuesService.connectDeviceId,
                },
                platform: data.platform,
                lang: lang,
                app_id: data.app_id,
                type: this.configService.deployTypeIsProd() ? LinkType.PRODUCTION : LinkType.INSIDER,
            }
        };

        if (data.service_id && !this.isV3Service(data?.service_id)) {
            json.params.service_id = data.service_id;
        }

        if (data.profile_id) {
            json.params.profile_id  = data.profile_id;
        }

        if (data.activity_id) {
            json.params.activity_id = data.activity_id;
        }

        return this.requestsService.make(
            this.configService.config.nimbusServer,
            this.valuesService.downloadAgentService,
            json,
            POST
        ).pipe(
            map(
                (res: any) => {
                    return res.result;
                }
            ),
            catchError((error) => {
                throw error;
            }),
        );
    }

    /**
     * This method is used to generate a link valid for validity seconds
     * @param {SharedLinkInfo} data Contains data for the request
     * @returns Response from server
     */
    public getShareLinkV2(data: SharedLinkInfo): Observable<any> {
        const json: any = {
            id: 1,
            jsonrpc: this.valuesService.jsonrpc,
            method: 'get_share_link',
            params: {
                connect_source: {
                    user_token: this.cookieService.get(this.valuesService.cookieToken),
                    app_id: this.valuesService.connectAppId,
                    device_id: this.valuesService.connectDeviceId,
                },
                type: this.configService.deployTypeIsProd() ? LinkType.PRODUCTION : LinkType.INSIDER,
            }
        };

        if (data?.product){
            json.params.product = data.product;
        }

        if (data?.profile_id){
            json.params.profile_id = data.profile_id;
        }

        if (data?.service_id && !this.isV3Service(data?.service_id)){
            json.params.service_id = data.service_id;
        }

        if (data?.activity_id) {
            json.params.activity_id = data.activity_id;
        }

        if (data?.qr_code) {
            json.params.qr_code = data.qr_code;
        }

        if (data?.qr_code_type) {
            json.params.qr_code_type = data.qr_code_type;
        }

        return this.requestsService.make(
            this.configService.config.nimbusServer,
            this.valuesService.downloadAgentService,
            json,
            POST
        ).pipe(
            map(
                (res: any) => {
                    return res.result;
                }
            ),
            catchError((error) => {
                throw error;
            }),
        );
    }

    /**
     * Replace hostname with current hostname in download link
     * @private
     * @param {string} link
     * @return {*}  {string}
     * @memberof DownloadAgentService
     */
    private _replaceHostname(link: string): string {
        const url = new URL(link);
        url.hostname = location.hostname;
        return url.toString();
    }
}
